import { useMemo } from 'react';
import { useQuery } from 'react-query';
import CustomerService from '../../services/customersService';

/**
 * Portal account capability flags for one Customer, via
 * CustomerService.getPortalStatus / GET .../customers/{id}/portal-user/status.
 *
 * The household query already carries the raw PortalUser record; what it
 * cannot carry is the derived can-* and email-validation properties, which the backend
 * computes per request (Customers.js#getPortalUserStatus).
 */
const useCustomerPortalStatusQuery = ({
  organizationId,
  propertyId,
  customerId,
  options = {},
}: {
  organizationId: string,
  propertyId: string,
  customerId: ?string,
  options?: { enabled?: boolean },
}) => {
  const customerService = useMemo(() => new CustomerService(), []);

  return useQuery(
    ['getPortalUserStatus', organizationId, propertyId, customerId],
    // `enabled` is not a guard for refetch(), which callers use after an
    // action -- without this the service would be addressed with an
    // undefined id and build a malformed URL.
    () =>
      customerId
        ? customerService.getPortalStatus(
            customerId,
            propertyId,
            organizationId,
          )
        : Promise.resolve({}),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      ...options,
      enabled: Boolean(customerId) && options.enabled !== false,
    },
  );
};

export default useCustomerPortalStatusQuery;
